import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../../lib/api";

const ENDED = new Set(["ended", "failed", "no_answer", "busy"]);

const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

// One browser phone line: places calls, answers incoming, exposes live state.
export function useSoftphone() {
  const clientRef = useRef(null);
  const sdkCallRef = useRef(null);
  const audioRef = useRef(null);
  const [call, setCall] = useState(null);
  const [status, setStatus] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [error, setError] = useState(null);
  const [connectedAt, setConnectedAt] = useState(null);

  const ensureClient = useCallback(async () => {
    if (clientRef.current) return clientRef.current;
    const { data } = await apiClient.post("/softphone/token");
    const { SignalWire, StaticCredentialProvider } = await import("@signalwire/js");
    const client = new SignalWire(new StaticCredentialProvider({ token: data.token }));
    await client.register();
    clientRef.current = { client, lineAddress: data.line_address };
    return clientRef.current;
  }, []);

  const reset = useCallback(() => {
    sdkCallRef.current = null;
    setCall(null); setMuted(false); setHeld(false); setConnectedAt(null);
    setTimeout(() => setStatus((s) => (ENDED.has(s) || s === "ended" ? "idle" : s)), 2500);
  }, []);

  const joinLine = useCallback(async (callDoc) => {
    setCall(callDoc); setError(null); setStatus("connecting");
    try {
      const { client, lineAddress } = await ensureClient();
      const c = await client.dial(lineAddress, { audio: true, video: false });
      sdkCallRef.current = c;
      c.remoteStream$?.subscribe((stream) => {
        if (audioRef.current) { audioRef.current.srcObject = stream; audioRef.current.play().catch(() => {}); }
      });
      c.status$?.subscribe((s) => {
        if (s === "disconnected" || s === "destroyed" || s === "failed") {
          apiClient.post(`/softphone/calls/${callDoc.id}/hangup`).catch(() => {});
          setStatus((prev) => (ENDED.has(prev) ? prev : "ended"));
          reset();
        }
      });
    } catch (e) {
      setError(errText(e, "Microphone or phone line unavailable"));
      setStatus("failed");
      reset();
    }
  }, [ensureClient, reset]);

  const dial = useCallback(async (target) => {
    setError(null);
    try {
      const { data } = await apiClient.post("/softphone/dial", target);
      await joinLine(data.call);
    } catch (e) { setError(errText(e, "Call failed to start")); setStatus("failed"); reset(); }
  }, [joinLine, reset]);

  const answer = useCallback(async (inc) => {
    try {
      const { data } = await apiClient.post(`/softphone/incoming/${inc.id}/answer`, { source: inc.source });
      await joinLine(data.call);
    } catch (e) { setError(errText(e, "Couldn't answer")); }
  }, [joinLine]);

  // Live status from the server (ringing → connected → ended) while a call exists.
  useEffect(() => {
    if (!call?.id) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await apiClient.get(`/softphone/calls/${call.id}`);
        setStatus((prev) => (prev === "ended" ? prev : data.status));
        if (data.status === "connected") setConnectedAt((v) => v || Date.now());
        if (data.status === "failed") setError(data.error ? "The call couldn't be placed — the phone carrier rejected it." : "The call couldn't be placed.");
        if (ENDED.has(data.status)) { try { await sdkCallRef.current?.hangup(); } catch { /* already gone */ } reset(); }
      } catch { /* transient */ }
    }, 1500);
    return () => clearInterval(t);
  }, [call?.id, reset]);

  const hangup = useCallback(async () => {
    try { await sdkCallRef.current?.hangup(); } catch { /* noop */ }
    if (call?.id) apiClient.post(`/softphone/calls/${call.id}/hangup`).catch(() => {});
    setStatus("ended"); reset();
  }, [call?.id, reset]);

  const toggleMute = useCallback(async () => {
    const c = sdkCallRef.current;
    try { await c?.self?.toggleMute(); } catch { /* noop */ }
    setMuted((m) => !m);
  }, []);

  const toggleHold = useCallback(async () => {
    const c = sdkCallRef.current;
    try { await c?.toggleHold(); } catch {
      try { await c?.self?.toggleMute(); await c?.toggleIncomingAudio(); } catch { /* noop */ }
    }
    setHeld((h) => !h);
  }, []);

  const sendDigit = useCallback((d) => { sdkCallRef.current?.sendDigits(d).catch(() => {}); }, []);

  const transfer = useCallback(async (target) => {
    if (!call?.id) return;
    try {
      await apiClient.post(`/softphone/calls/${call.id}/transfer`, target);
      setTimeout(() => hangup(), 1500);
    } catch (e) { setError(errText(e, "Transfer failed")); }
  }, [call?.id, hangup]);

  return { call, status, muted, held, error, connectedAt, audioRef, dial, answer, hangup, toggleMute, toggleHold, sendDigit, transfer, setError };
}
